import {ContainerStatusEntity, restrictionType} from "@ots-frontend/ots-data"
import {createContext, useContext} from "react"

// The icons and the rules for which apply mirror FieldGo's (ots-android-lib's ContainerStatusIndicators and
// OTSStatusIcon). The SVGs are generated from Material Icons (Google, Apache License 2.0), plus a classic set from
// the Tabler Icons the web showed before (MIT); see libs/shared/public/container-status/README.md.

export const containerStatusIconNames = [
  "message",
  "lact-meter",
  "lact-meter-disabled",
  "do-not-use",
  "do-not-use-scheduled",
  "do-not-dispatch",
  "do-not-dispatch-scheduled",
  "ignore-max-non-primary-volume",
  "water-pipeline",
  "oil-pipeline",
  "local-tenant-fallback",
  "hunting-restriction",
  "weather-restriction",
  "road-restriction",
  "rejection-restriction",
  "stale-poll",
] as const

export type ContainerStatusIconName = typeof containerStatusIconNames[number]
export type ContainerStatusIconVariant = "outlined" | "filled"
/** `fieldgo` matches the Android apps; `classic` is the web's earlier Tabler set, same names, sizes and colors. */
export type ContainerStatusIconSet = "fieldgo" | "classic"

/** The set used where neither ContainerStatusIconSetProvider nor an `iconSet` prop picks one. */
export const defaultContainerStatusIconSet: ContainerStatusIconSet = "classic"

const ContainerStatusIconSetContext = createContext<ContainerStatusIconSet>(defaultContainerStatusIconSet)

/** Picks the icon set for every container status icon below it; the classic set when there is none. */
export const ContainerStatusIconSetProvider = ContainerStatusIconSetContext.Provider

export const useContainerStatusIconSet = () => useContext(ContainerStatusIconSetContext)

export type ContainerStatusIconDescriptor = {
  name: ContainerStatusIconName
  label: string
  src: string
}

export const containerStatusIconLabels: Record<ContainerStatusIconName, string> = {
  "message": "Container message",
  "lact-meter": "LACT meter",
  "lact-meter-disabled": "LACT meter unavailable",
  "do-not-use": "Do Not Use",
  "do-not-use-scheduled": "Do Not Use — scheduled reset",
  "do-not-dispatch": "Do Not Dispatch",
  "do-not-dispatch-scheduled": "Do Not Dispatch — scheduled reset",
  "ignore-max-non-primary-volume": "Ignoring max non-primary volume",
  "water-pipeline": "Water transported by pipeline",
  "oil-pipeline": "Oil transported by pipeline",
  "local-tenant-fallback": "Using tenant fallback data",
  "hunting-restriction": "Hunting restriction",
  "weather-restriction": "Weather restriction",
  "road-restriction": "Road restriction",
  "rejection-restriction": "Rejection restriction",
  "stale-poll": "No recent poll",
}

const restrictionIcons = new Map<unknown, ContainerStatusIconName>([
  [restrictionType.hunting, "hunting-restriction"],
  [restrictionType.weather, "weather-restriction"],
  [restrictionType.road, "road-restriction"],
  [restrictionType.rejection, "rejection-restriction"],
])

/** Icons with a -dark.svg twin, drawn so they stay visible on a dark surface. */
const darkVariantIcons = new Set<ContainerStatusIconName>([
  "oil-pipeline",
  "message",
  "lact-meter",
  "ignore-max-non-primary-volume",
])

export const getContainerStatusIconSrc = (
  name: ContainerStatusIconName,
  variant: ContainerStatusIconVariant = "filled",
  set: ContainerStatusIconSet = defaultContainerStatusIconSet,
  colorScheme: "light" | "dark" = "light",
): string => `${import.meta.env.BASE_URL}container-status/${set === "classic" ? "classic/" : ""}${variant}/${name}${colorScheme === "dark" && darkVariantIcons.has(name) ? "-dark" : ""}.svg`

/**
 * The color each cargo's pipeline icon is drawn in (every set and variant), so other marks for that cargo match it.
 * Oil turns light on a dark surface, as its -dark.svg does.
 */
export const getPipelineCargoColor = (cargo: "water" | "oil", colorScheme: "light" | "dark" = "light"): string =>
  cargo === "oil" ? (colorScheme === "dark" ? "#e4e4e7" : "#A15C12") : "#1689C7"

const descriptor = (
  name: ContainerStatusIconName,
  variant: ContainerStatusIconVariant,
  set: ContainerStatusIconSet,
  colorScheme: "light" | "dark",
): ContainerStatusIconDescriptor => ({
  name,
  label: containerStatusIconLabels[name],
  src: getContainerStatusIconSrc(name, variant, set, colorScheme),
})

export const resolveContainerStatusIcons = (
  status: ContainerStatusEntity | undefined,
  variant: ContainerStatusIconVariant = "filled",
  set: ContainerStatusIconSet = defaultContainerStatusIconSet,
  colorScheme: "light" | "dark" = "light",
  /** Web only: the page decides staleness from its display options. */
  flags: {stalePoll?: boolean} = {},
): ContainerStatusIconDescriptor[] => {
  if (!status) return []

  const icons: ContainerStatusIconDescriptor[] = []
  const add = (name: ContainerStatusIconName) => icons.push(descriptor(name, variant, set, colorScheme))

  if (flags.stalePoll) add("stale-poll")
  if (status.containerMessages?.length) add("message")

  if (status.lactMeters?.length) {
    add(status.lactMeters.every(meter => meter.doNotUse) ? "lact-meter-disabled" : "lact-meter")
  }

  if (status.containerDoNotUse) {
    add(status.containerClearDoNotUseScheduledDttm ? "do-not-use-scheduled" : "do-not-use")
  }

  if (status.containerDoNotDispatch) {
    add(status.containerClearDoNotDispatchScheduledDttm ? "do-not-dispatch-scheduled" : "do-not-dispatch")
  }

  if (status.containerDispatchIgnoreMaxNonPrimaryVolume) add("ignore-max-non-primary-volume")
  if (status.siteTransportViaPipeline) add("water-pipeline")
  if (status.siteTransportOilViaPipeline) add("oil-pipeline")
  if (status.metadata?.connection?.source === "LOCAL_TENANT_FALLBACK") add("local-tenant-fallback")

  // In the order the restrictions come, once per type, as ContainerStatusIndicatorResolver does on Android.
  new Set(status.restrictions?.map(restriction => restriction.type)).forEach(type => {
    const name = type && restrictionIcons.get(type)
    if (name) add(name)
  })

  return icons
}

